import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// @mui
import { Box, Card, Stack, Button, Container, Typography, LinearProgress } from '@mui/material';
// hooks
import useSettings from '../../hooks/useSettings';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
// the shared quiz report, embedded and locked to this program+batch
import QuizResults from './QuizResults';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// utils
import axiosInstance from '../../utils/axios';
import { APIS } from '../../utils/serviceUrls';

// ---------------------------------------------------------------------------
// Dedicated quiz report for ONE batch (cohort) of a program.
//
// The cohort header + KPIs come from the program document and the batch
// comparison; the per-quiz / per-student / attempt tables are the shared
// QuizResults report, embedded and locked to this eventId + batchId.
//
// batchId === 'none' is the pseudo-batch for attempts that carry no batch
// (submitted before batches existed, or by a student in no batch).
// ---------------------------------------------------------------------------

export default function ProgramBatchReport() {
  const { eventId = '', batchId = '' } = useParams();
  const navigate = useNavigate();
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [program, setProgram] = useState<any>(null);
  const [comparison, setComparison] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const isUnassigned = batchId === 'none';

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axiosInstance.get(APIS.EVENTS.BY_ID(eventId)).then((r) => r.data?.data ?? r.data),
      axiosInstance
        .get(APIS.QUIZ_ATTEMPTS.BATCH_COMPARISON, { params: { eventId } })
        .then((r) => r.data?.data ?? r.data),
    ])
      .then(([ev, cmp]) => {
        if (cancelled) return;
        setProgram(ev);
        setComparison(Array.isArray(cmp) ? cmp : []);
      })
      .catch((e: any) => {
        if (cancelled) return;
        enqueueSnackbar(e?.message || 'Failed to load batch report', { variant: 'error' });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [eventId, enqueueSnackbar]);

  const programTitle = program?.title || program?.name || 'Program';

  const batch = useMemo(
    () => (program?.batches || []).find((b: any) => String(b._id) === String(batchId)),
    [program, batchId]
  );

  const stats = useMemo(
    () =>
      comparison.find((r: any) =>
        isUnassigned ? !r.batchId : String(r.batchId) === String(batchId)
      ),
    [comparison, batchId, isUnassigned]
  );

  const batchName = isUnassigned ? 'Unassigned (no batch)' : batch?.name || 'Batch';
  const enrolled = (batch?.students || []).length;
  // How many of the enrolled students have actually submitted a quiz — the
  // participation signal that a raw average hides.
  const submitters = stats?.students ?? 0;
  const participation = enrolled > 0 ? Math.round((submitters / enrolled) * 100) : null;

  const fmtDate = (d?: string) => (d ? String(d).slice(0, 10) : '—');

  const kpis = [
    { label: 'Attempts', value: stats?.attempts ?? 0 },
    { label: 'Quizzes taken', value: stats?.quizzes ?? 0 },
    { label: 'Average score', value: stats ? `${stats.avgPercentage}%` : '—' },
    { label: 'Pass rate', value: stats ? `${stats.passRate}%` : '—' },
  ];

  return (
    <Page title={`${programTitle} — ${batchName}`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`${batchName} — Quiz Report`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Programs', href: PATH_DASHBOARD.events },
            { name: programTitle, href: PATH_DASHBOARD.programBatches(eventId) },
            { name: batchName },
          ]}
          action={
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:pie-chart-outline" />}
                onClick={() =>
                  navigate(
                    `${PATH_DASHBOARD.programQuizAnalysis}?eventId=${eventId}&batchId=${batchId}`
                  )
                }
              >
                Quiz analysis
              </Button>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="eva:arrow-back-outline" />}
                onClick={() => navigate(PATH_DASHBOARD.programBatches(eventId))}
              >
                All batches
              </Button>
            </Stack>
          }
        />

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Cohort header */}
        <Card sx={{ p: 2.5, mb: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1">{batchName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {isUnassigned
                  ? 'Attempts submitted with no batch — before batches existed, or by students in no batch.'
                  : `${fmtDate(batch?.startDate)} → ${fmtDate(batch?.endDate)} · ${enrolled} student${
                      enrolled === 1 ? '' : 's'
                    } enrolled`}
              </Typography>
            </Box>
            {!isUnassigned && participation !== null && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Label color={participation >= 50 ? 'success' : 'warning'}>
                  {participation}% participation
                </Label>
                <Typography variant="caption" color="text.secondary">
                  {submitters} of {enrolled} have submitted a quiz
                </Typography>
              </Stack>
            )}
          </Stack>
        </Card>

        {/* Batch KPIs */}
        <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
          {kpis.map((k) => (
            <Card key={k.label} sx={{ p: 2.5, flex: 1, minWidth: 150 }}>
              <Typography variant="h4">{k.value}</Typography>
              <Typography variant="caption" color="text.secondary">
                {k.label}
              </Typography>
            </Card>
          ))}
        </Stack>

        {!loading && !stats && (
          <Card sx={{ p: 5, textAlign: 'center', mb: 3 }}>
            <Iconify
              icon="eva:clipboard-outline"
              sx={{ width: 40, height: 40, color: 'text.disabled', mb: 1 }}
            />
            <Typography variant="subtitle1">No quiz attempts in this batch yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Results appear here once a student in this batch submits a program lecture quiz.
            </Typography>
          </Card>
        )}

        {/* Shared quiz report, locked to this program + batch */}
        <QuizResults scope="program" eventId={eventId} batchId={batchId} embedded />
      </Container>
    </Page>
  );
}
